import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Activity, RefreshCw } from 'lucide-react'
import { api, type PolicyRuntimeMetricsCounters } from '../lib/api'
import { queries } from '../lib/queries'
import { Button } from '../components/ui/button'
import { Skeleton } from '../components/ui/skeleton'

export const Route = createFileRoute('/admin/metrics')({
  head: () => ({
    meta: [{ title: 'Metrics | Admin | Unbreakable' }],
  }),
  component: AdminMetricsPage,
})

const metricLabels: Array<{ key: keyof PolicyRuntimeMetricsCounters; label: string }> = [
  { key: 'applyRequests', label: 'Apply requests' },
  { key: 'applyNoopAlreadyApplied', label: 'Apply no-op (already applied)' },
  { key: 'applyReinstalls', label: 'Apply reinstalls' },
  { key: 'applyRemoveBeforeInstallSent', label: 'Remove-before-install sent' },
  { key: 'removeRequests', label: 'Remove requests' },
  { key: 'removeDeferredSourcesRemain', label: 'Remove deferred (sources remain)' },
  { key: 'removeSkippedAlreadyRemovingOrRaceLost', label: 'Remove skipped (already removing/race lost)' },
  { key: 'removeEnqueueStarted', label: 'Remove enqueue started' },
  { key: 'removeEnqueueSucceeded', label: 'Remove enqueue succeeded' },
  { key: 'removeEnqueueFailed', label: 'Remove enqueue failed' },
  { key: 'notNowReceived', label: 'NotNow received' },
  { key: 'notNowRetryScheduled', label: 'NotNow retries scheduled' },
  { key: 'notNowRetrySkippedStale', label: 'NotNow retries skipped stale' },
  { key: 'notNowRetryPushSent', label: 'NotNow retry push sent' },
  { key: 'notNowRetryPushFailed', label: 'NotNow retry push failed' },
]

function AdminMetricsPage() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: queries.admin.policyMetrics(),
    queryFn: () => api.getAdminPolicyRuntimeMetrics(),
    refetchInterval: 5000,
  })

  const counters = data?.counters ?? null
  const startedAt = data?.startedAt ?? null

  const healthSummary = useMemo(() => {
    if (!counters) return null
    const hardFailures = counters.removeEnqueueFailed + counters.notNowRetryPushFailed
    if (hardFailures > 0) return { tone: 'text-red-700', text: 'Failures detected (check enqueue/push counters).' }
    if (counters.removeSkippedAlreadyRemovingOrRaceLost > 0 || counters.notNowRetrySkippedStale > 0) return { tone: 'text-amber-700', text: 'Race/stale guards are actively preventing duplicate work.' }
    return { tone: 'text-emerald-700', text: 'No failures observed in current process runtime.' }
  }, [counters])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Policy Runtime Metrics</h2>
          <p className="text-gray-500 mt-1">Live counters from backend policy/schedule/webhook logic.</p>
        </div>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Activity className="h-5 w-5" />Runtime Health</h3>
          <p className="text-sm text-gray-500">Process started: {startedAt ? new Date(startedAt).toLocaleString() : '—'}</p>
        </div>
        <div className="p-6">
          {isLoading && !counters ? (
            <Skeleton className="h-5 w-64" />
          ) : (
            <p className={`text-sm font-medium ${healthSummary?.tone ?? 'text-gray-700'}`}>{healthSummary?.text ?? 'No data yet.'}</p>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Counters</h3>
          <p className="text-sm text-gray-500">Polled every 5s. Values reset on backend restart.</p>
        </div>
        <div className="p-6">
          {!counters ? (
            <p className="text-sm text-gray-500">No counter data available.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {metricLabels.map((m) => (
                <div key={m.key} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs text-gray-500">{m.label}</p>
                  <p className="mt-1 text-xl font-semibold text-gray-900">{counters[m.key]}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
